import { apiBaseUrl } from "../../client/src/main.ts";
import { toast } from "react-hot-toast";
import { endpoints } from "./docsData.ts";
import { Highlight, type RenderProps, themes } from "prism-react-renderer";

const codeBlockStyle = {
  padding: "16px",
  borderRadius: "8px",
  overflowX: "auto",
  fontSize: "13px",
  lineHeight: "1.6",
  margin: 0,
  maxWidth: "100%",
  boxSizing: "border-box",
  wordBreak: "break-word",
  whiteSpace: "pre-wrap",
} as const;

const copyButtonStyle = {
  position: "absolute",
  top: "8px",
  right: "8px",
  padding: "6px 12px",
  backgroundColor: "#2d2d2d",
  color: "#d4d4d4",
  border: "1px solid #4d4d4d",
  borderRadius: "4px",
  cursor: "pointer",
  fontSize: "12px",
  fontFamily: "sans-serif",
  transition: "background-color 0.2s",
} as const;

const codeBlockContainerStyle = {
  position: "relative",
  marginBottom: "8px",
};

const sectionStyle = {
  marginBottom: "32px",
  padding: "clamp(12px, 3vw, 20px)",
  backgroundColor: "var(--surface-alt)",
  borderRadius: "var(--radius)",
  border: "1px solid var(--border)",
  overflowX: "hidden" as const,
};

const headerStyle = {
  marginTop: 0,
  marginBottom: "8px",
  color: "#4fc3f7",
};

const badgeStyle = (color: string) => ({
  display: "inline-block",
  padding: "4px 8px",
  borderRadius: "4px",
  fontSize: "12px",
  fontWeight: 600,
  backgroundColor: color,
  color: "white",
  marginRight: "8px",
});

const endpointUrlStyle = {
  fontSize: "14px",
  wordBreak: "break-all",
  overflowWrap: "anywhere",
} as const;

const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text).then(
    () => {
      toast.success("Copied to clipboard!");
    },
    () => {
      toast.error("Failed to copy");
    },
  );
};

const CodeBlock = (
  { code, language = "json" }: { code: string; language?: string },
) => (
  <div style={codeBlockContainerStyle}>
    <button
      type="button"
      style={copyButtonStyle}
      onClick={() => copyToClipboard(code)}
      onMouseEnter={(e) => {
        (e.target as HTMLButtonElement).style.backgroundColor = "#3d3d3d";
      }}
      onMouseLeave={(e) => {
        (e.target as HTMLButtonElement).style.backgroundColor = "#2d2d2d";
      }}
    >
      Copy
    </button>
    <Highlight theme={themes.vsDark} code={code} language={language}>
      {({ style, tokens, getLineProps, getTokenProps }: RenderProps) => (
        <pre style={{ ...codeBlockStyle, ...style }}>
          {tokens.map((line, i) => (
            <div key={i} {...getLineProps({ line })}>
              {line.map((token, key) => (
                <span key={key} {...getTokenProps({ token })} />
              ))}
            </div>
          ))}
        </pre>
      )}
    </Highlight>
  </div>
);

const EndpointSection = (
  {
    name,
    method,
    endpoint,
    description,
    auth,
    requestBody,
    responseBody,
    example,
  }: {
    name: string;
    method: "POST";
    endpoint: string;
    description: string;
    auth: string;
    requestBody: Record<
      string,
      { type: string; required?: boolean; description: string }
    >;
    responseBody: Record<string, { type: string; description: string }>;
    example: { request: unknown; response: unknown };
  },
) => (
  <div style={sectionStyle}>
    <h3 style={headerStyle}>{name}</h3>
    <div style={{ marginBottom: "16px" }}>
      <span style={badgeStyle("#2e7d32")}>{method}</span>
      <code style={endpointUrlStyle}>{apiBaseUrl}{endpoint}</code>
    </div>
    <p style={{ marginBottom: "16px", opacity: 0.9 }}>{description}</p>

    <div style={{ marginBottom: "16px" }}>
      <strong>Authentication:</strong> {auth}
    </div>

    {Object.keys(requestBody).length > 0 && (
      <div style={{ marginBottom: "16px" }}>
        <strong>Request Body:</strong>
        <div style={{ marginTop: "8px" }}>
          {Object.entries(requestBody).map(([key, value]) => (
            <div key={key} style={{ marginLeft: "16px", marginBottom: "8px" }}>
              <code style={{ color: "#ce9178" }}>{key}</code>{" "}
              <span style={{ opacity: 0.7 }}>({value.type})</span>
              {value.required && (
                <span style={{ color: "#f48771" }}>*required</span>
              )}
              <div
                style={{ marginLeft: "16px", fontSize: "13px", opacity: 0.8 }}
              >
                {value.description}
              </div>
            </div>
          ))}
        </div>
      </div>
    )}

    {Object.keys(responseBody).length > 0 && (
      <div style={{ marginBottom: "16px" }}>
        <strong>Response:</strong>
        <div style={{ marginTop: "8px" }}>
          {Object.entries(responseBody).map(([key, value]) => (
            <div key={key} style={{ marginLeft: "16px", marginBottom: "8px" }}>
              <code style={{ color: "#ce9178" }}>{key}</code>{" "}
              <span style={{ opacity: 0.7 }}>({value.type})</span>
              <div
                style={{ marginLeft: "16px", fontSize: "13px", opacity: 0.8 }}
              >
                {value.description}
              </div>
            </div>
          ))}
        </div>
      </div>
    )}

    <div>
      <strong>Example:</strong>
      <div style={{ marginTop: "8px" }}>
        <div style={{ marginBottom: "8px", fontSize: "13px", opacity: 0.8 }}>
          Request:
        </div>
        <CodeBlock code={JSON.stringify(example.request, null, 2)} />
      </div>
      <div style={{ marginTop: "12px" }}>
        <div style={{ marginBottom: "8px", fontSize: "13px", opacity: 0.8 }}>
          Response:
        </div>
        <CodeBlock code={JSON.stringify(example.response, null, 2)} />
      </div>
    </div>
  </div>
);

export const ApiDocs = () => (
  <div
    style={{
      padding: "clamp(10px, 3vw, 20px)",
      maxWidth: "1000px",
      margin: "0 auto",
    }}
  >
    <h1 style={{ marginTop: 0, marginBottom: "8px" }}>API Documentation</h1>
    <p style={{ opacity: 0.85, marginBottom: "32px" }}>
      Integrate Supergreen WhatsApp automation into your application using our
      HTTP API.
    </p>

    <div style={sectionStyle}>
      <h2 style={{ marginTop: 0, marginBottom: "16px" }}>Getting Started</h2>
      <p>
        All API endpoints require authentication using your account's secret
        token. You can find this token in your WhatsApp account settings on the
        main dashboard.
      </p>
      <p style={{ marginTop: "12px" }}>
        <strong>Base URL:</strong> <code>{apiBaseUrl}</code>
      </p>
      <p style={{ marginTop: "12px" }}>
        All endpoints accept JSON request bodies and return JSON responses. Make
        sure to set the <code>Content-Type: application/json</code> header.
      </p>
    </div>

    <div style={sectionStyle}>
      <h2 style={{ marginTop: 0, marginBottom: "16px" }}>
        Node.js Quick Start
      </h2>
      <CodeBlock
        language="javascript"
        code={`// Install the client (optional)
// npm install node-fetch

const fetch = require('node-fetch');

const API_BASE = '${apiBaseUrl}';
const YOUR_TOKEN = 'your-secret-token-here';
const YOUR_PHONE = 'your-whatsapp-number';

async function sendMessage(toNumber, message) {
  const response = await fetch(API_BASE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      endpoint: 'sendMessage',
      payload: {
        fromNumber: YOUR_PHONE,
        toNumber: toNumber,
        message: message,
        linkPreview: false,
        token: YOUR_TOKEN
      }
    })
  });
  return response.json();
}

// Example usage
sendMessage('972501234567@c.us', 'Hello from Node.js!')
  .then(result => console.log('Message sent:', result))
  .catch(err => console.error('Error:', err));`}
      />
    </div>

    <div style={sectionStyle}>
      <h2 style={{ marginTop: 0, marginBottom: "16px" }}>Webhook Events</h2>
      <p>
        When you configure a webhook URL, Supergreen will send POST requests to
        your server whenever messages or events are received. The webhook
        payload follows this structure:
      </p>
      <CodeBlock
        code={JSON.stringify(
          {
            network: "whatsapp",
            chat: {
              id: "972509876543@c.us",
              username: "John Doe",
              title: "Optional Group Title",
            },
            author: {
              id: "972509876543@c.us",
              username: "John Doe",
            },
            text: "Hello! This is a message from a user.",
            time: 1703251200000,
          },
          null,
          2,
        )}
      />
    </div>

    <h2 style={{ marginTop: "32px", marginBottom: "16px" }}>Endpoints</h2>

    {endpoints.map((ep, i) => <EndpointSection key={i} {...ep} />)}

    <div style={sectionStyle}>
      <h2 style={{ marginTop: 0, marginBottom: "16px" }}>Error Handling</h2>
      <p>
        All API endpoints return standard HTTP status codes. Successful requests
        return 200 OK. Errors return appropriate status codes with JSON error
        details:
      </p>
      <CodeBlock
        code={JSON.stringify(
          {
            error: "Authentication failed",
            message: "Invalid token provided",
          },
          null,
          2,
        )}
      />
      <p style={{ marginTop: "12px" }}>Common error codes:</p>
      <ul style={{ marginLeft: "20px", opacity: 0.9 }}>
        <li>400 - Bad Request (invalid parameters)</li>
        <li>401 - Unauthorized (invalid token)</li>
        <li>404 - Not Found (endpoint doesn't exist)</li>
        <li>500 - Internal Server Error</li>
      </ul>
    </div>
  </div>
);
